import React from 'react';
import { useTranslation } from 'react-i18next';
import { WordFormationItem } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: WordFormationItem[];
    teacherMode: boolean;
    onWordAnswered?: (word: string, correct: boolean, hintUsed?: boolean) => void;
    sourceLanguage?: string;
    targetLanguage?: string;
}

function normalize(s: string): string {
    return s.trim().toLowerCase();
}

function isCorrect(answer: string, item: WordFormationItem): boolean {
    const norm = normalize(answer);
    if (norm === normalize(item.expected_form)) return true;
    return (item.acceptable_forms ?? []).some((f) => normalize(f) === norm);
}

// Splits on any run of 3+ underscores rather than requiring an exact literal ("______") — the
// LLM has produced the blank with varying underscore counts (e.g. "___"), which an exact match
// would miss entirely, silently displaying the placeholder as literal text. Collapses anything
// after the first blank back into a single trailing segment, so a stray second underscore run
// elsewhere in the sentence (a real generation bug seen in practice — the blank duplicated after
// the sentence's own closing punctuation) never produces a second, misplaced gap.
function splitOnBlank(sentence: string): [string, string] {
    const raw = sentence.split(/_{3,}/);
    return [raw[0], raw.slice(1).join('')];
}

const WordFormationSection: React.FC<Props> = ({ lessonId, items, teacherMode, onWordAnswered, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [answers, setAnswers] = useLessonPersistedState<Record<number, string>>(lessonId, 'wordformation.answers', {});
    const [checked, setChecked] = useLessonPersistedState(lessonId, 'wordformation.checked', false);
    const [score, setScore] = useLessonPersistedState<number | null>(lessonId, 'wordformation.score', null);
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'wordformation.showTranslation', {});
    const [explanationFallback, setExplanationFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'wordformation.explanationFallback', {});
    // Pre-check icons (sentence_with_blank, base_word) can hint at the transformation — opening
    // one before "Check Answers" is clicked flags that item for a reduced score at Finish.
    const [showSentenceTranslation, setShowSentenceTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'wordformation.showSentenceTranslation', {});
    const [sentenceFallback, setSentenceFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'wordformation.sentenceFallback', {});
    const [showBaseWordTranslation, setShowBaseWordTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'wordformation.showBaseWordTranslation', {});
    const [baseWordFallback, setBaseWordFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'wordformation.baseWordFallback', {});
    const [hintUsed, setHintUsed] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'wordformation.hintUsed', {});

    const markHint = (i: number) => {
        if (checked) return;
        setHintUsed((prev) => (prev[i] ? prev : { ...prev, [i]: true }));
    };

    // Prepopulated with the base word (see lesson-qcard input below) rather than left blank, so
    // the student edits it into the right form instead of typing from scratch — but an
    // untouched, unedited base word is never accidentally graded correct, since it normally
    // differs from expected_form/acceptable_forms.
    const answerFor = (i: number, item: WordFormationItem) => answers[i] ?? item.base_word;

    const checkAnswers = () => {
        let s = 0;
        items.forEach((item, i) => {
            const correct = isCorrect(answerFor(i, item), item);
            if (correct) s++;
            onWordAnswered?.(item.base_word, correct, hintUsed[i]);
        });
        setScore(s);
        setChecked(true);
    };

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.wordform.kicker', 'Practice')}</div>
                <h2>{t('sections.wordform.title', 'Word Formation')}</h2>
                <p className="lesson-panel-desc">{t('sections.wordform.desc', 'Change the base word to fit the gap.')}</p>
            </div>
            <div className="lesson-qgrid">
                {items.map((item, i) => {
                    const parts = splitOnBlank(item.sentence_with_blank);
                    const correct = checked && isCorrect(answerFor(i, item), item);
                    return (
                        <div className="lesson-qcard" key={i}>
                            <div className="lesson-qnum lesson-word-target">
                                {t('sections.question_n', 'Question {{n}}', { n: i + 1 })} · {t('sections.wordform.base_word', 'base word:')} <strong>{item.base_word}</strong>
                                <TranslateToggle
                                    text={item.base_word}
                                    fallback={baseWordFallback[i]}
                                    onFallbackFetched={(text) => setBaseWordFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showBaseWordTranslation[i]}
                                    onToggle={() => { markHint(i); setShowBaseWordTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            <div className="lesson-blank-sentence lesson-word-target">
                                {parts[0]}<span className="lesson-gap">______</span>{parts[1]}
                                <TranslateToggle
                                    text={item.sentence_with_blank}
                                    fallback={sentenceFallback[i]}
                                    onFallbackFetched={(text) => setSentenceFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showSentenceTranslation[i]}
                                    onToggle={() => { markHint(i); setShowSentenceTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            <input
                                className={`lesson-input-line${checked ? (correct ? ' correct' : ' incorrect') : ''}`}
                                disabled={checked}
                                value={answerFor(i, item)}
                                onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                            />
                            {(checked || teacherMode) && (
                                <div className="lesson-reveal lesson-word-target">
                                    <strong>{item.expected_form}</strong>
                                    <div className="lesson-reveal-explanation">
                                        {item.explanation}
                                        <TranslateToggle
                                            text={item.explanation}
                                            native={item.explanation_native}
                                            fallback={explanationFallback[i]}
                                            onFallbackFetched={(text) => setExplanationFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showTranslation[i]}
                                            onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="lesson-actions-row">
                <button className="lesson-btn" onClick={checkAnswers} disabled={checked}>
                    {t('sections.check_answers', 'Check Answers')}
                </button>
                {score !== null && (
                    <div className="lesson-score-pill">{t('sections.score', '{{score}} / {{total}} correct', { score, total: items.length })}</div>
                )}
            </div>
        </div>
    );
};

export default WordFormationSection;
